import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { galleryItems } from '../data/gallery';
import { company } from '../data/content';
import { ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon, ClockIcon, WaveIcon, PhoneIcon } from './icons';
const ease = [0.23, 1, 0.32, 1] as const;
export function Gallery() {
  const [index, setIndex] = useState(0);
  const active = galleryItems[index];
  const step = useCallback((direction: 1 | -1) => {
    setIndex(current => (current + direction + galleryItems.length) % galleryItems.length);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step]);
  return <section id="gallery" className="w-full border-t border-navy-700 bg-navy-800 py-20 lg:py-28" data-id="element-265" data-mp="raglir2csvb0">
      <div className="mx-auto max-w-content px-5 lg:px-8" data-id="element-266" data-mp="w9jlvy7e2dcn">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-267" data-mp="113fgqrscvk3l">
          <div className="max-w-2xl" data-id="element-268" data-mp="152wjhdgee352">
            <p className="text-sm font-semibold text-sky-300" data-id="element-269" data-mp="15629cuwup91u">Project gallery</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-270" data-mp="8f9rbrqcx6xf">
              Nine backyards, start to finish.
            </h2>
          </div>
          <p className="text-sm text-navy-200 sm:text-right" data-id="element-271" data-mp="hctwvcv95pdu">
            Every photo is our own crew&apos;s work.
            <br className="hidden sm:block" data-id="element-272" data-mp="mbdgv7qacr3k" /> Use the arrows or pick a project below.
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[minmax(0,8fr)_minmax(0,4fr)]" data-id="element-273" data-mp="1svwip2hyofhc">
          {/* Stage */}
          <div className="relative overflow-hidden rounded-lg bg-navy-900" data-id="element-274" data-mp="3tvrtbec6a7h0">
            <AnimatePresence mode="wait" initial={false} data-id="element-275" data-mp="3txupkg3x4wu6">
              <motion.img key={active.src} src={active.src} alt={active.alt} initial={{
              opacity: 0,
              scale: 1.02
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.28,
              ease
            }} className="h-[280px] w-full object-cover sm:h-[420px] lg:h-[520px]" data-id="element-276" data-mp="3pt4wt04k3ydh" />
            </AnimatePresence>

            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-4 sm:p-5" data-id="element-277" data-mp="3kv3bs9nklozd">
              <span className="rounded-md bg-navy-900/85 px-3.5 py-2 font-display text-[13px] font-bold tabular-nums text-white" data-id="element-278" data-mp="3g4d42pro9a1i">
                {String(index + 1).padStart(2, '0')}
                <span className="text-navy-300" data-id="element-279" data-mp="2scfuz6flww43"> / {String(galleryItems.length).padStart(2, '0')}</span>
              </span>
              <span className="flex gap-2" data-id="element-280" data-mp="12xx06af4huzz">
                <button type="button" onClick={() => step(-1)} aria-label="Previous project" className="flex h-11 w-11 items-center justify-center rounded-full bg-navy-900/85 text-white transition-colors duration-150 ease-out hover:bg-navy-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300" data-id="element-281" data-mp="12w3w92vw9ovo">
                  <ChevronLeftIcon className="h-5 w-5" data-id="element-282" data-mp="1qn9cvk9nsl71" />
                </button>
                <button type="button" onClick={() => step(1)} aria-label="Next project" className="flex h-11 w-11 items-center justify-center rounded-full bg-sky-400 text-navy-900 transition-colors duration-150 ease-out hover:bg-sky-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-200" data-id="element-283" data-mp="1qkvgn838t2xw">
                  <ChevronRightIcon className="h-5 w-5" data-id="element-284" data-mp="lhku4kflgbbu" />
                </button>
              </span>
            </div>
          </div>

          {/* Details */}
          <div className="flex flex-col rounded-lg bg-navy-900 p-6 sm:p-7" data-id="element-285" data-mp="go87ugye0gm9">
            <AnimatePresence mode="wait" initial={false} data-id="element-286" data-mp="brre978f5a1s">
              <motion.div key={active.title} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.22,
              ease
            }} data-id="element-287" data-mp="7q66u1enlnyz">
                <span className="inline-flex rounded-full bg-sky-400 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-navy-900" data-id="element-288" data-mp="vf79t94pw6wv">
                  {active.category}
                </span>
                <h3 className="mt-4 font-display text-2xl font-bold leading-tight text-white" data-id="element-289" data-mp="3g5g671r5sf32">
                  {active.title}
                </h3>
                <p className="mt-1.5 text-sm font-semibold text-sky-200" data-id="element-290" data-mp="3b5k1f0an0jmg">{active.location}</p>
                <p className="mt-4 text-[15px] leading-relaxed text-navy-100" data-id="element-291" data-mp="1qpdk7k3hdiie">{active.note}</p>

                <dl className="mt-6 space-y-3 border-t border-navy-600 pt-5 text-sm" data-id="element-292" data-mp="1vogrzcaui2u2">
                  <div className="flex items-center gap-3" data-id="element-293" data-mp="1zoqw0jf2uj6a">
                    <WaveIcon className="h-4 w-4 shrink-0 text-sky-300" data-id="element-294" data-mp="1zn61uuwod28y" />
                    <dt className="sr-only" data-id="element-295" data-mp="24hacpp929ufb">Scope</dt>
                    <dd className="text-navy-100" data-id="element-296" data-mp="29fbrhxzgf7kz">{active.scope}</dd>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-297" data-mp="2edn2ic1hjo4s">
                    <ClockIcon className="h-4 w-4 shrink-0 text-sky-300" data-id="element-298" data-mp="2ec29wryqef9k" />
                    <dt className="sr-only" data-id="element-299" data-mp="2jeb1pkzvbba3">Timeline</dt>
                    <dd className="text-navy-100" data-id="element-300" data-mp="2nebhb8ypdsiq">{active.duration} on site</dd>
                  </div>
                </dl>
              </motion.div>
            </AnimatePresence>

            <div className="mt-auto pt-7" data-id="element-301" data-mp="p1bgnccpxovf">
              <a href="#contact" className="flex w-full items-center justify-center gap-2 rounded-md bg-sky-400 px-5 py-3.5 font-display text-[15px] font-bold text-navy-900 transition-colors duration-150 ease-out hover:bg-sky-300" data-id="element-302" data-mp="szsgdatt7x2t">
                Get this for your pool
                <ArrowRightIcon className="h-4 w-4" data-id="element-303" data-mp="25cxlju6rg8cp" />
              </a>
              <a href={company.phoneHref} className="mt-3 flex items-center justify-center gap-2 text-sm font-semibold text-sky-200 transition-colors duration-150 ease-out hover:text-white" data-id="element-304" data-mp="21a17mgs610f3">
                <PhoneIcon className="h-4 w-4" data-id="element-305" data-mp="2aapmfdpdv4t1" />
                Or call {company.phoneDisplay}
              </a>
            </div>
          </div>
        </div>

        {/* Thumbnail rail */}
        <ul aria-label="Choose a project" className="mt-4 flex snap-x gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-306" data-mp="2k1kc6s1u8mj8">
          {galleryItems.map((item, itemIndex) => {
          const isActive = itemIndex === index;
          return <li key={item.src} className="shrink-0 snap-start" data-id="element-307" data-mp="3co1zohg8f1tw" data-mpi={itemIndex}>
                <button type="button" onClick={() => setIndex(itemIndex)} aria-current={isActive ? 'true' : undefined} aria-label={`${item.title}, ${item.location}`} className={`relative block h-20 w-28 overflow-hidden rounded-md transition-[opacity,box-shadow] duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300 sm:h-24 sm:w-36 ${isActive ? 'opacity-100 ring-2 ring-sky-400' : 'opacity-55 hover:opacity-90'}`} data-id="element-308" data-mp="3hmvce7np2ot0">
                  <img src={item.src} alt="" className="h-full w-full object-cover" data-id="element-309" data-mp="2ayeuk04qmozk" />
                </button>
              </li>;
        })}
        </ul>
      </div>
    </section>;
}